import React from "react";
import { MessageCircle, Search, Truck, ExternalLink } from "lucide-react";

export default function AdminOrders({
  filteredOrders,
  orderSearch,
  setOrderSearch,
  setOrderStatus,
  orderSocialMedia,
  orderStatuses,
  formatPaymentStatus,
  formatOrderAmount,
  createEkartShipment,
}) {
  // Blank weight uses the server-configured EKART_WEIGHT_GRAMS default.
  const [shipmentFormByOrder, setShipmentFormByOrder] = React.useState({});
  const [verifiedRetryByOrder, setVerifiedRetryByOrder] = React.useState({});
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [paymentFilter, setPaymentFilter] = React.useState("all");
  const [sourceFilter, setSourceFilter] = React.useState("all");

  const normalizedStatus = (order) => {
    const status = String(order.status || "new").trim().toLowerCase().replace(/[\s-]+/g, "_");
    if (status === "canceled") return "cancelled";
    if (["ready_for_dispatch", "ready_to_dispatch"].includes(status)) return "ready";
    if (status === "dispatched") return "shipped";
    return status;
  };
  const visibleOrders = filteredOrders.filter((order) => {
    const status = normalizedStatus(order);
    const payment = String(order.payment_status || "unknown").trim().toLowerCase();
    const source = String(order.source || order.order_source || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
    const items = Array.isArray(order.items) ? order.items : [];
    const manual = ["social_media", "phone", "walk_in", "whatsapp", "manual", "manual_order", "instagram", "facebook"].includes(source) || order.product_slug === "manual-custom-order" || items.some((item) => item?.product_slug === "manual-custom-order");
    return (statusFilter === "all" || status === statusFilter) &&
      (paymentFilter === "all" || (paymentFilter === "unpaid" ? payment !== "paid" : payment === paymentFilter)) &&
      (sourceFilter === "all" || (sourceFilter === "manual" ? manual : !manual));
  });
  const resetFilters = () => {
    setStatusFilter("all");
    setPaymentFilter("all");
    setSourceFilter("all");
  };
  const statusTone = (status) => {
    const tones = {
      new: "bg-blue-50 text-blue-700 border-blue-200",
      confirmed: "bg-indigo-50 text-indigo-700 border-indigo-200",
      processing: "bg-amber-50 text-amber-800 border-amber-200",
      printed: "bg-purple-50 text-purple-700 border-purple-200",
      ready: "bg-orange-50 text-orange-700 border-orange-200",
      shipped: "bg-cyan-50 text-cyan-800 border-cyan-200",
      delivered: "bg-green-50 text-green-700 border-green-200",
      cancelled: "bg-red-50 text-red-700 border-red-200",
    };
    return tones[status] || "bg-stone-50 text-stone-700 border-stone-200";
  };
  const fulfilmentStages = ["new", "confirmed", "processing", "printed", "ready", "shipped", "delivered"];
  const currentStage = (status) => fulfilmentStages.indexOf(status);

  const updateShipmentField = (orderNumber, field, value) => {
    setShipmentFormByOrder((current) => ({
      ...current,
      [orderNumber]: { ...(current[orderNumber] || {}), [field]: value },
    }));
  };

  return (
    <div
      className="mt-6"
      data-testid="admin-orders"
    >
      <div className="flex flex-col lg:flex-row gap-4 justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="overline">
            Orders
          </div>

          <div className="text-xs text-stone-500 mt-1">
            {visibleOrders.length}{" "}
            order
            {visibleOrders.length === 1
              ? ""
              : "s"} after filters
          </div>
        </div>

        <div className="relative w-full lg:w-80">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
          />

          <input
            value={orderSearch}
            onChange={(e) =>
              setOrderSearch(e.target.value)
            }
            placeholder="Search order, customer, item, design ID, pincode, waybill…"
            className="input-pmt pl-9"
          />
        </div>
      </div>

      <section className="mb-5 rounded-lg border border-[#E6DFD5] bg-white p-3 sm:p-4" aria-label="Order filters">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="text-xs font-semibold text-stone-600">
            Fulfilment status
            <select className="input-pmt mt-1 w-full text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              {orderStatuses.map((status) => <option key={status} value={status}>{status === "ready" ? "Ready for dispatch" : status === "shipped" ? "Dispatched" : status.replace(/_/g, " ")}</option>)}
            </select>
          </label>
          <label className="text-xs font-semibold text-stone-600">
            Payment
            <select aria-label="Payment" className="input-pmt mt-1 w-full text-sm" value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
              <option value="all">All payment states</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="unpaid">Not marked paid</option>
            </select>
          </label>
          <label className="text-xs font-semibold text-stone-600">
            Order source
            <select className="input-pmt mt-1 w-full text-sm" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
              <option value="all">All sources</option>
              <option value="online">Website orders</option>
              <option value="manual">Manual / social orders</option>
            </select>
          </label>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-stone-500" aria-live="polite">Showing <strong>{visibleOrders.length}</strong> of {filteredOrders.length} matching orders</p>
          <button type="button" onClick={resetFilters} className="btn-outline-pmt text-xs">Clear filters</button>
        </div>
      </section>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-5">
        {[
          ["New", "new"],
          ["Confirmed", "confirmed"],
          ["Processing", "processing"],
          ["Printed", "printed"],
          ["Ready for dispatch", "ready"],
          ["Dispatched", "shipped"],
          ["Delivered", "delivered"],
          ["Cancelled", "cancelled"],
        ].map(([label, status]) => {
          const count = filteredOrders.filter(
            (order) => normalizedStatus(order) === status
          ).length;

          const selected = statusFilter === status;
          return (
            <button
              key={status}
              type="button"
              aria-pressed={selected}
              aria-label={`Filter orders by ${label}: ${count}`}
              onClick={() => setStatusFilter(selected ? "all" : status)}
              className={`card-pmt px-3 py-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D95328] ${selected ? "border-[#D95328] bg-orange-50 ring-1 ring-[#D95328]" : "hover:border-stone-400"}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-stone-500">
                {label}
              </div>
              <div
                className="mt-1 text-2xl font-black"
                style={{
                  fontFamily: "Barlow Condensed, sans-serif",
                }}
              >
                {count}
              </div>
              <div className="mt-1 text-[10px] text-stone-500">{selected ? "Filtered · click to clear" : "Click to filter"}</div>
            </button>
          );
        })}
      </div>

      <div className="overflow-auto border border-[#E6DFD5]">
        <table className="min-w-[1380px] w-full table-fixed text-sm">
          <thead className="text-xs uppercase text-stone-500 bg-[#F3EDE2]">
            <tr>
              <th className="text-left py-3 px-3">
                Order #
              </th>

              <th className="text-left px-3">
                Customer
              </th>

              <th className="text-left px-3">
                Items
              </th>

              <th className="text-left px-3">
                Amount
              </th>

              <th className="text-left px-3">
                Payment
              </th>

              <th className="text-left px-3 min-w-[160px] w-[160px]">
                Status
              </th>

              <th className="text-left px-3 min-w-[470px] w-[470px]">
                Shipment
              </th>
              <th className="text-left px-3">
                Shipping / Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {visibleOrders.map((o) => {
              const paymentStatus =
                formatPaymentStatus(o.payment_status);

              const isPaid =
                String(o.payment_status || "").trim().toLowerCase() === "paid";
              const normalized = normalizedStatus(o);
              const isCancelled = normalized === "cancelled";
              const stage = currentStage(normalized);

              const creatingTimestamp = o.ekart_shipment_requested_at
                ? new Date(o.ekart_shipment_requested_at).getTime()
                : NaN;
              const staleCreating = o.ekart_shipment_state === "creating" &&
                (!Number.isFinite(creatingTimestamp) || Date.now() - creatingTimestamp > 15 * 60 * 1000);
              const needsShipmentVerification = ["failed", "unknown"].includes(o.ekart_shipment_state) || staleCreating;
              const retryVerified = Boolean(verifiedRetryByOrder[o.order_number]);

              return (
                <tr
                  key={o.order_number}
                  className="border-t border-[#E6DFD5] align-top"
                >
                  <td className="py-4 px-3 font-mono text-xs">
                    <div className="font-bold">
                      {o.order_number}
                    </div>

                    {o.created_at && (
                      <div className="text-[10px] text-stone-400 mt-1">
                        {new Date(
                          o.created_at
                        ).toLocaleString()}
                      </div>
                    )}
                  </td>

                  <td className="px-3">
                    <div className="font-semibold">
                      {o.full_name}
                    </div>

                    <div className="text-xs text-stone-500 mt-1">
                      {o.mobile}
                    </div>

                    <div className="text-xs text-stone-500">
                      {o.email}
                    </div>

                    <div className="text-xs text-stone-500 mt-1 break-words whitespace-normal" title={[
                        o.address,
                        o.address_line,
                        o.address_line_2,
                        o.landmark,
                        o.city,
                        o.district,
                        o.state,
                        o.pincode,
                      ].map((part) => String(part || "").trim()).filter(Boolean).filter((part, index, parts) => parts.indexOf(part) === index).join(", ")}>
                      {[
                        o.address,
                        o.address_line,
                        o.address_line_2,
                        o.landmark,
                        o.city,
                        o.district,
                        o.state,
                        o.pincode,
                      ]
                        .map((part) => String(part || "").trim())
                        .filter(Boolean)
                        .filter((part, index, parts) => parts.indexOf(part) === index)
                        .join(", ") || "Address not provided"}
                    </div>
                  </td>

                  <td className="px-3">
                    <ul className="text-xs list-disc pl-4 space-y-1">
                      {(Array.isArray(o.items) ? o.items : []).map(
                        (it, i) => (
                          <li key={i}>
                            {it.product_name}{" "}
                            ×{" "}
                            {it.quantity || 1}

                            {it.design_id
                              ? ` (${it.design_id})`
                              : ""}

                            {it.size
                              ? ` • ${it.size}`
                              : ""}

                            {it.color
                              ? ` • ${it.color}`
                              : ""}

                            {it.printing_method
                              ? ` • ${it.printing_method}`
                              : ""}

                            {it.gift_options && Object.entries(it.gift_options).some(([, value]) => String(value || "").trim())
                              ? ` • Gift details: ${Object.entries(it.gift_options).filter(([, value]) => String(value || "").trim()).map(([key, value]) => `${(it.gift_option_labels && it.gift_option_labels[key]) || key.replace(/_/g, " ")}: ${value}`).join(", ")}`
                              : ""}
                            {it.custom_name ? ` • Name: ${it.custom_name}` : ""}
                            {it.custom_number ? ` • Number: ${it.custom_number}` : ""}
                            {it.custom_text ? ` • Text: ${it.custom_text}` : ""}
                          </li>
                        )
                      )}
                    </ul>
                  </td>

                  <td className="px-3 whitespace-nowrap">
                    <div className="font-bold text-[#D95328]">
                      {formatOrderAmount(o)}
                    </div>
                  </td>

                  <td className="px-3 whitespace-nowrap">
                    <div
                      className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase border ${
                        isPaid
                          ? "bg-green-50 text-green-700 border-green-200"
                          : "bg-yellow-50 text-yellow-700 border-yellow-200"
                      }`}
                    >
                      {paymentStatus}
                    </div>

                    <div className="text-xs text-stone-500 mt-2">
                      {o.payment_method || "—"}
                    </div>

                    <div className="mt-3 space-y-2 text-[10px] leading-snug">
                      {[
                        {
                          label: "Telegram",
                          sentAt: o.telegram_notification_sent_at,
                          error: o.telegram_notification_error,
                        },
                        {
                          label: "Business email",
                          sentAt: o.business_email_notification_sent_at,
                          error: o.business_email_notification_error,
                        },
                        {
                          label: "Customer email",
                          sentAt: o.invoice_email_sent_at,
                          error: o.invoice_email_error,
                        },
                      ].map((notification) => (
                        <div key={notification.label} className="min-w-0">
                          <div className="font-semibold text-stone-600">
                            {notification.label}
                          </div>
                          {notification.error ? (
                            <div
                              className="text-red-700 break-words"
                              title={notification.error}
                            >
                              Failed: {notification.error}
                            </div>
                          ) : notification.sentAt ? (
                            <div className="text-green-700">
                              Sent {new Date(notification.sentAt).toLocaleString()}
                            </div>
                          ) : (
                            <div className="text-stone-400">
                              Not recorded
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </td>

                  <td className="px-3 min-w-[160px] w-[160px]">
                    <span className={`inline-flex max-w-full items-center rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide break-words ${statusTone(normalized)}`}>
                      {normalized === "ready" ? "Ready for dispatch" : normalized === "shipped" ? "Dispatched" : normalized.replace(/_/g, " ")}
                    </span>
                    {normalized !== "cancelled" && (
                      <div className="mt-3 mb-2" aria-label="Fulfilment progress">
                        <div className="flex items-center justify-between gap-1 text-[9px] text-stone-400">
                          <span>New</span><span>Production</span><span>Ready</span><span>Delivered</span>
                        </div>
                        <div className="mt-1 flex gap-1" aria-hidden="true">
                          {fulfilmentStages.map((item, index) => (
                            <span key={item} className={`h-1.5 flex-1 rounded-full ${stage >= index ? "bg-[#D95328]" : "bg-stone-200"}`} />
                          ))}
                        </div>
                      </div>
                    )}

                    <select
                      className="input-pmt text-xs py-1 w-full min-w-0"
                      value={normalized}
                      onChange={(e) =>
                        setOrderStatus(
                          o.order_number,
                          e.target.value
                        )
                      }
                    >
                      {orderStatuses.map((s) => (
                        <option key={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td className="px-3 min-w-[470px] w-[470px]">
                    {o.ekart_tracking_id ? (
                      <div className="space-y-1 text-xs">
                        <div className="font-semibold text-green-700">Created: {o.ekart_tracking_id}</div>
                        {o.ekart_waybill && <div className="text-stone-500">Waybill: {o.ekart_waybill}</div>}
                        {o.ekart_tracking_url && (
                          <a href={o.ekart_tracking_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#D95328] underline">
                            Track shipment <ExternalLink size={11} />
                          </a>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-xs font-semibold uppercase text-stone-600">Create when physically packed</div>
                        {isCancelled && (
                          <div className="text-xs text-red-700" role="status">
                            Shipment creation is disabled because this order is cancelled.
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            ["weight", "Weight (g)", 50000, "500"],
                            ["length", "Length (cm)", 500, "30"],
                            ["width", "Width (cm)", 500, "20"],
                            ["height", "Height (cm)", 500, "5"],
                          ].map(([field, label, max, placeholder]) => (
                            <label key={field} className="text-[11px] text-stone-600">
                              {label}
                              <input type="number" min="1" max={max} step="1"
                                value={shipmentFormByOrder[o.order_number]?.[field] ?? ""}
                                placeholder={placeholder}
                                onChange={(e) => updateShipmentField(o.order_number, field, e.target.value)}
                                className="input-pmt mt-1 w-full text-xs"
                              />
                            </label>
                          ))}
                        </div>
                        {needsShipmentVerification && (
                          <label className="flex items-start gap-2 text-xs text-amber-800">
                            <input type="checkbox" checked={retryVerified}
                              onChange={(e) => setVerifiedRetryByOrder((current) => ({ ...current, [o.order_number]: e.target.checked }))}
                              className="mt-0.5"
                            />
                            <span>{staleCreating ? "This request appears stuck. I checked Ekart and confirmed no shipment exists; retry may create a duplicate if that check is wrong." : "I checked Ekart and confirmed no shipment exists. Retry may create a new shipment."}</span>
                          </label>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const form = shipmentFormByOrder[o.order_number] || {};
                            // Verification is one-time consent for this attempt.
                            // Require the admin to re-check Ekart before any retry.
                            setVerifiedRetryByOrder((current) => ({ ...current, [o.order_number]: false }));
                            createEkartShipment(
                              o.order_number,
                              form.weight === undefined || form.weight === "" ? undefined : Number(form.weight),
                              form.length === undefined || form.length === "" ? undefined : Number(form.length),
                              form.width === undefined || form.width === "" ? undefined : Number(form.width),
                              form.height === undefined || form.height === "" ? undefined : Number(form.height),
                              needsShipmentVerification && retryVerified
                            );
                          }}
                          disabled={
                            isCancelled ||
                            (o.ekart_shipment_state === "creating" && !staleCreating) ||
                            (needsShipmentVerification && !retryVerified) ||
                            ["weight", "length", "width", "height"].some((key) => {
                              const value = shipmentFormByOrder[o.order_number]?.[key];
                              return value !== undefined && value !== "" &&
                                (!Number.isInteger(Number(value)) || Number(value) < 1 || Number(value) > (key === "weight" ? 50000 : 500));
                            })
                          }
                          className="btn-outline-pmt text-xs whitespace-nowrap disabled:opacity-40"
                          title={isCancelled ? "Shipment creation is blocked for cancelled orders" : needsShipmentVerification ? "Retry only after checking the Ekart dashboard" : "Create Ekart shipment using the packed parcel measurements"}
                        >
                          <Truck size={12} />
                          {o.ekart_shipment_state === "creating" && !staleCreating ? "Creating…" : needsShipmentVerification ? "Retry Ekart" : "Create Ekart"}
                        </button>
                        <div className="text-[10px] text-stone-400">Blank fields use the Render defaults.</div>
                      </div>
                    )}
                  </td>
                  <td className="px-3 space-y-2">
                    <a href={orderSocialMedia(o)} target="_blank" rel="noreferrer" className="btn-whatsapp text-xs whitespace-nowrap">
                      <MessageCircle size={12} />
                      WhatsApp
                    </a>
                  </td>
                </tr>
              );
            })}

            {visibleOrders.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="py-12 text-center text-stone-500"
                >
                  {orderSearch.trim() || statusFilter !== "all" || paymentFilter !== "all" || sourceFilter !== "all"
                    ? "No orders match these filters. Clear filters to see all matching orders."
                    : "No orders yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
